/**
 * `/provisioning-approvals` (OCP-298 + OCP-479) - operator queue for
 * large-tenant sales leads. A non-presale (trial/invite) redeem that
 * declared a 250+ team size is blocked from auto-provisioning and captured
 * here so an operator can follow up, then APPROVE (provisions the tenant
 * through the standard pipeline with the approved sizing) or REJECT. Closes
 * the dedicated-server cost-abuse vector (wiki General/DOC-5).
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDate } from '../lib/format-time';

type Status = 'pending' | 'approved' | 'rejected';

interface ApprovalRow {
  id: string;
  contact_email: string;
  contact_name: string | null;
  requested_team_size: string | null;
  desired_subdomain: string | null;
  status: Status;
  reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  tenant_id: string | null;
  created_at: string;
}

const FILTERS: Array<{ value: Status | 'all'; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

export function AdminProvisioningApprovalsPage() {
  const [status, setStatus] = useState<Status | 'all'>('pending');
  const [items, setItems] = useState<ApprovalRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState<ApprovalRow | null>(null);
  const [approving, setApproving] = useState<ApprovalRow | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const qs = status === 'all' ? '' : `?status=${status}`;
    try {
      const res = await api<{ items: ApprovalRow[] }>(`/v1/admin/provisioning-approvals${qs}`);
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? `Load failed (HTTP ${err.status}).` : 'Load failed.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Provisioning approvals</h1>
        <p className="mt-1 text-xs text-gray-500">
          Large-team (250+) redeems from non-presale codes are blocked from
          auto-provisioning and land here as sales leads. Follow up, then approve
          (provisions the tenant on the approved sizing) or reject. Presale
          (prepaid) codes provision directly and never appear here.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as Status | 'all')}
          className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        >
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <div className="text-xs text-gray-500">
          {loading ? 'Loading…' : `${items.length} shown`}
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          No requests.
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Requested</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Team size</th>
                <th className="px-3 py-2">Desired subdomain</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((r) => (
                <tr key={r.id} className={r.status !== 'pending' ? 'opacity-60' : ''}>
                  <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-gray-500">
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-medium">{r.contact_email}</div>
                    {r.contact_name && (
                      <div className="text-gray-500">{r.contact_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-medium">{r.requested_team_size ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {r.desired_subdomain ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={r.status} />
                    {r.status === 'rejected' && r.reason && (
                      <div className="mt-0.5 text-xs text-gray-500">{r.reason}</div>
                    )}
                    {r.status === 'approved' && r.tenant_id && (
                      <div className="mt-0.5 text-xs text-gray-500">
                        provisioned{r.desired_subdomain ? ` → ${r.desired_subdomain}` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.status === 'pending' && (
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setApproving(r)}
                          className="text-xs font-medium text-emerald-600 hover:underline"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => setRejecting(r)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approving && (
        <ApproveModal
          row={approving}
          onCancel={() => setApproving(null)}
          onDone={async () => {
            setApproving(null);
            await load();
          }}
        />
      )}

      {rejecting && (
        <RejectModal
          row={rejecting}
          onCancel={() => setRejecting(null)}
          onDone={async () => {
            setRejecting(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ApproveModal({
  row,
  onCancel,
  onDone,
}: {
  row: ApprovalRow;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/provisioning-approvals/${row.id}/approve`, {
        method: 'POST',
      });
      await onDone();
    } catch (err) {
      // 422 = provision precondition failed (subdomain now taken, code gone);
      // the request stays pending so it can be retried after a fix.
      if (err instanceof ApiError && err.status === 422) {
        setError('Provision preconditions failed - the desired subdomain may be taken or the code is no longer redeemable. The request stays pending; fix the cause and retry.');
      } else if (err instanceof ApiError && err.status === 409) {
        setError('This request is no longer pending (already decided).');
      } else {
        setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Failed.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">Approve &amp; provision</h2>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          Provisions a dedicated orboto tenant for{' '}
          <code className="font-mono text-xs">{row.contact_email}</code> at{' '}
          <code className="font-mono text-xs">{row.desired_subdomain ?? '(no subdomain)'}</code>{' '}
          on the approved sizing ({row.requested_team_size ?? 'unknown'} seats). This
          runs the standard provision pipeline and consumes one use of the
          attempted code - the same flow a normal redeem takes.
        </p>
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void submit()}
            className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? 'Provisioning…' : 'Approve & provision'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: Status }) {
  const cfg = {
    pending: { label: 'pending', cls: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200' },
    approved: { label: 'approved', cls: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
    rejected: { label: 'rejected', cls: 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
  }[status];
  return <span className={`rounded border px-2 py-0.5 text-xs ${cfg.cls}`}>{cfg.label}</span>;
}

function RejectModal({
  row,
  onCancel,
  onDone,
}: {
  row: ApprovalRow;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/provisioning-approvals/${row.id}/reject`, {
        method: 'POST',
        body: { reason: reason.trim() || undefined },
      });
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">Reject approval request</h2>
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          Marks the lead from <code className="font-mono text-xs">{row.contact_email}</code>{' '}
          ({row.requested_team_size ?? 'unknown'} seats) as rejected. Optional reason for the audit trail:
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="e.g. spam / no response / duplicate"
          className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
        />
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void submit()}
            className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}
